import React, { useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import {
  ActionHub,
  FeedbackModal,
  ACTION_IDS,
  HELP_CENTER_URL,
} from '@edulastic/common'
import { appFeedbackApi, segmentApi } from '@edulastic/api'
import { roleuser, tutorials } from '@edulastic/constants'
import ActionHubJoyride from '../ActionHubJoyride'
import { getActionHubElementId } from '../ActionHubJoyride/constants'
import {
  isPearyVisibleInActionHubSelector,
  isPremiumUserSelector,
} from '../../../author/src/selectors/user'
import {
  areViewedTutorialsLoadedSelector,
  getActionHubJoyrideEligible,
  getShouldDeferActionHubJoyrideAssignmentPage,
  setViewedTutorialsAction,
  viewedTutorialsSelector,
} from '../../../tutorials/tutorialReducer'
import { getShouldDeferActionHubJoyrideOnDashboard } from '../../../author/Dashboard/ducks'
import { getUserRole } from '../../../student/Login/ducks'

const trackActionHubEvent = (action, pagePath = window.location.pathname) => {
  segmentApi.genericEventTrack('ActionHub', { action, pagePath })
}

const ENABLED_ACTION_IDS = [
  ACTION_IDS.PEARY,
  ACTION_IDS.FEEDBACK,
  ACTION_IDS.ARTICLES,
  ACTION_IDS.SUPPORT,
]

const PEARY_PREMIUM_TOOLTIP = 'Upgrade to Premium to access Peary AI.'

const ActionHubContainer = ({
  isPearyVisibleInActionHub,
  isPremiumUser,
  isActionHubJoyrideEligible,
  viewedTutorials,
  setViewedTutorials,
  shouldDeferActionHubJoyride,
  shouldDeferActionHubJoyrideOnDashboard,
  viewedTutorialsLoaded,
  userRole,
}) => {
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false)
  const [joyrideExpanded, setJoyrideExpanded] = useState(false)

  const handlePearyClick = useCallback(() => {
    trackActionHubEvent('Peary AI')
    segmentApi.genericEventTrack('PearyChatbot', {
      pearyActions: 'Open',
      pagePath: window.location.pathname,
    })
    window.pear?.chat?.open()
  }, [])

  const handleFeedbackSelect = useCallback(() => {
    trackActionHubEvent('Feedback')
    setFeedbackModalVisible(true)
  }, [])

  const handleSupportSelect = useCallback(() => {
    trackActionHubEvent('Support')
  }, [])
  const handleArticlesSelect = useCallback(() => {
    trackActionHubEvent('Articles')
    window.open(HELP_CENTER_URL, '_blank', 'noopener,noreferrer')
  }, [])

  const actions = useMemo(() => {
    const actionDefinitions = {
      [ACTION_IDS.PEARY]: {
        label: 'Peary AI',
        isVisible: () => isPearyVisibleInActionHub,
        disabled: !isPremiumUser,
        tooltipTitle: !isPremiumUser ? PEARY_PREMIUM_TOOLTIP : undefined,
        onSelect: handlePearyClick,
      },
      [ACTION_IDS.FEEDBACK]: {
        label: 'Submit Feedback',
        isVisible: () => true,
        onSelect: handleFeedbackSelect,
      },
      [ACTION_IDS.ARTICLES]: {
        label: 'Help Center',
        isVisible: () => true,
        onSelect: handleArticlesSelect,
      },
      [ACTION_IDS.SUPPORT]: {
        label: 'Contact Support',
        isVisible: () => true,
        onSelect: handleSupportSelect,
      },
    }

    return ENABLED_ACTION_IDS.map((id) => ({
      id,
      ...actionDefinitions[id],
    }))
  }, [
    handlePearyClick,
    handleFeedbackSelect,
    handleSupportSelect,
    isPearyVisibleInActionHub,
    isPremiumUser,
  ])

  const handleFabClick = useCallback(({ expanded }) => {
    if (expanded) {
      trackActionHubEvent('ActionHubClick')
    }
  }, [])

  const handleFeedbackOpen = useCallback(() => {
    segmentApi.genericEventTrack('Feedback', {
      action: 'Open',
      featureName: 'ActionHub',
      pagePath: window.location.pathname,
    })
  }, [])

  const handleFeedbackClose = useCallback(() => {
    segmentApi.genericEventTrack('Feedback', {
      action: 'Close',
      featureName: 'ActionHub',
      pagePath: window.location.pathname,
    })
    setFeedbackModalVisible(false)
  }, [])

  const handleFeedbackSubmit = useCallback(async (payload) => {
    await appFeedbackApi.sendFeedback(payload)
  }, [])

  const handleFeedbackSubmitSuccess = useCallback(({ feedbackDetails }) => {
    segmentApi.genericEventTrack('Feedback', {
      action: 'Submit',
      featureName: 'ActionHub',
      feedbackDetails,
      pagePath: window.location.pathname,
    })
  }, [])

  const handleFeedbackSubmitFailure = useCallback(() => {
    segmentApi.genericEventTrack('Feedback', {
      action: 'SubmitFailed',
      featureName: 'ActionHub',
      pagePath: window.location.pathname,
    })
  }, [])

  const showJoyride = useMemo(() => {
    const isActionHubJoyrideNotSeen =
      viewedTutorialsLoaded &&
      !viewedTutorials.includes(tutorials.actionHubJoyride)
    if (isActionHubJoyrideNotSeen) {
      const pathname = window.location.pathname
      if (pathname === '/author/dashboard') {
        return (
          isActionHubJoyrideEligible && !shouldDeferActionHubJoyrideOnDashboard
        )
      }
      if (pathname === '/author/assignments') {
        return (
          ![roleuser.TEACHER, roleuser.STUDENT].includes(userRole) &&
          !shouldDeferActionHubJoyride
        )
      }
    }
  }, [
    isActionHubJoyrideEligible,
    shouldDeferActionHubJoyride,
    shouldDeferActionHubJoyrideOnDashboard,
    window.location.pathname,
    viewedTutorials,
    viewedTutorialsLoaded,
    userRole,
  ])

  return (
    <>
      <ActionHub
        actions={actions}
        feedbackOpen={feedbackModalVisible}
        onFabClick={handleFabClick}
        joyrideExpanded={joyrideExpanded}
        getJoyrideElementId={getActionHubElementId}
      />
      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={handleFeedbackClose}
        onOpen={handleFeedbackOpen}
        onSubmit={handleFeedbackSubmit}
        onSubmitSuccess={handleFeedbackSubmitSuccess}
        onSubmitFailure={handleFeedbackSubmitFailure}
      />
      {showJoyride && (
        <ActionHubJoyride
          isPearyVisibleInActionHub={isPearyVisibleInActionHub}
          viewedTutorials={viewedTutorials}
          setViewedTutorials={setViewedTutorials}
          onExpandMenu={() => setJoyrideExpanded(true)}
          onCollapseMenu={() => setJoyrideExpanded(false)}
        />
      )}
    </>
  )
}

const enhance = compose(
  connect(
    (state) => ({
      isPearyVisibleInActionHub: isPearyVisibleInActionHubSelector(state),
      isPremiumUser: isPremiumUserSelector(state),
      isActionHubJoyrideEligible: getActionHubJoyrideEligible(state),
      viewedTutorials: viewedTutorialsSelector(state),
      shouldDeferActionHubJoyride: getShouldDeferActionHubJoyrideAssignmentPage(
        state
      ),
      shouldDeferActionHubJoyrideOnDashboard: getShouldDeferActionHubJoyrideOnDashboard(
        state
      ),
      viewedTutorialsLoaded: areViewedTutorialsLoadedSelector(state),
      userRole: getUserRole(state),
    }),
    {
      setViewedTutorials: setViewedTutorialsAction,
    }
  )
)

export default enhance(ActionHubContainer)
